import { captureAuthTokenFromResponse, notifySessionExpired } from "@/auth/session.js";
import { isTokenExpired } from "@/auth/token.js";

export const API_CONFIG = {
    BASE_URL: ""
};

/** Default client-side timeout for API calls (ms). */
export const API_TIMEOUT_MS = 30000;

export const PUBLIC_ENDPOINTS = {
    LOGIN: "/api/v1/login",
    REGISTER: "/api/v1/register",
    PRODUCTS: "/api/v1/products",
    PRODUCTS_NEW: "/api/v1/products/new",
    PRODUCTS_RESTOCKED: "/api/v1/products/restocked",
    PRODUCT: (id) => `/api/v1/products/${id}`,
    PRODUCT_REVIEWS: (id) => `/api/v1/products/${id}/reviews`,
    PRODUCT_HIGHLIGHT: "/api/v1/products/highlight",
    PRODUCT_HIGHLIGHT_STRIPS: "/api/v1/products/highlight-strips",
    PRODUCT_HIGHLIGHT_AVAILABILITY: "/api/v1/products/highlight-availability",
    CATEGORIES: "/api/v1/categories",
    COUNTRIES: "/api/v1/countries",
    STORE_INFO: "/api/v1/store-info",
    PARTNER_SITE_PROMO_CLICKS: "/api/v1/partner-site-promo/clicks",
    FILE: (fileId) => `/api/v1/files/${fileId}`
};

export const CUSTOMER_ENDPOINTS = {
    USER: "/customer-secure/api/v1/users",
    CHANGE_PASSWORD: "/customer-secure/api/v1/users/change-password",
    WALKTHROUGH_COMPLETE: "/customer-secure/api/v1/users/walkthrough-complete",
    ADDRESSES: "/customer-secure/api/v1/addresses",
    ADDRESS: (addressId) => `/customer-secure/api/v1/addresses/${addressId}`,
    CART: "/customer-secure/api/v1/cart",
    CART_ITEMS: "/customer-secure/api/v1/cart/items",
    CART_ITEM: (productId) => `/customer-secure/api/v1/cart/items/${productId}`,
    LIKED_ITEMS: "/customer-secure/api/v1/liked-items",
    LIKED_ITEM_IDS: "/customer-secure/api/v1/liked-items/ids",
    LIKED_ITEM_ITEMS: "/customer-secure/api/v1/liked-items/items",
    LIKED_ITEM: (productId) => `/customer-secure/api/v1/liked-items/items/${productId}`,
    PURCHASED_PRODUCTS: "/customer-secure/api/v1/purchased-products",
    CHECKOUT: "/customer-secure/api/v1/orders/checkout",
    ORDERS: "/customer-secure/api/v1/orders",
    ORDER: (orderId) => `/customer-secure/api/v1/orders/${orderId}`,
    ORDER_CANCEL: (orderId) => `/customer-secure/api/v1/orders/${orderId}/cancel`,
    ORDER_RECEIPT: (orderId) => `/customer-secure/api/v1/orders/${orderId}/receipt`,
    PRODUCT_REVIEWS: (productId) => `/customer-secure/api/v1/products/${productId}/reviews`,
    RECOMMENDATIONS: "/customer-secure/api/v1/recommendations",
    UPLOAD_FILE: "/customer-secure/api/v1/files"
};

export const ADMIN_ENDPOINTS = {
    ORDERS: "/admin-secure/api/v1/orders",
    ORDER: (orderId) => `/admin-secure/api/v1/orders/${orderId}`,
    ORDER_STATUS: (orderId) => `/admin-secure/api/v1/orders/${orderId}/status`,
    ORDER_RECEIPT: (orderId) => `/admin-secure/api/v1/orders/${orderId}/receipt`,
    CUSTOMERS: "/admin-secure/api/v1/customers",
    CUSTOMER: (username) => `/admin-secure/api/v1/customers/${encodeURIComponent(username)}`,
    CUSTOMER_CART: (username) => `/admin-secure/api/v1/customers/${encodeURIComponent(username)}/cart`,
    CUSTOMER_LIKED_ITEMS: (username) => `/admin-secure/api/v1/customers/${encodeURIComponent(username)}/liked-items`,
    CARTS: "/admin-secure/api/v1/carts",
    LIKED_ITEMS: "/admin-secure/api/v1/liked-items",
    PRODUCTS: "/admin-secure/api/v1/products",
    PRODUCT_CATEGORIES: "/admin-secure/api/v1/products/categories",
    PRODUCT: (id) => `/admin-secure/api/v1/products/${id}`,
    PRODUCT_LOTS: (id) => `/admin-secure/api/v1/products/${id}/lots`,
    PRODUCT_LOT: (id, lotId) => `/admin-secure/api/v1/products/${id}/lots/${lotId}`,
    LOTS: "/admin-secure/api/v1/lots",
    LOT_NAMES: "/admin-secure/api/v1/lots/names",
    LOT: (lotId) => `/admin-secure/api/v1/lots/${lotId}`,
    PUBLISH: (id) => `/admin-secure/api/v1/products/${id}/publish`,
    UPLOAD_FILE: "/admin-secure/api/v1/files",
    STORE_SETTINGS: "/admin-secure/api/v1/store-settings",
    USER: "/admin-secure/api/v1/users",
    CHANGE_PASSWORD: "/admin-secure/api/v1/users/change-password",
    WALKTHROUGH_COMPLETE: "/admin-secure/api/v1/users/walkthrough-complete",
    REVIEWS: "/admin-secure/api/v1/reviews",
    REVIEW: (reviewId) => `/admin-secure/api/v1/reviews/${reviewId}`,
    REVIEW_RESTORE: (reviewId) => `/admin-secure/api/v1/reviews/${reviewId}/restore`,
    ADVERTISEMENTS: "/admin-secure/api/v1/advertisements",
    ADVERTISEMENT: (id) => `/admin-secure/api/v1/advertisements/${id}`,
    ADVERTISEMENT_PUBLISH: (id) => `/admin-secure/api/v1/advertisements/${id}/publish`,
    ADVERTISEMENT_APPROVE: (id) => `/admin-secure/api/v1/advertisements/${id}/approve`,
    ADVERTISEMENT_HIDE: (id) => `/admin-secure/api/v1/advertisements/${id}/hide`,
    EMPLOYEES: "/admin-secure/api/v1/employees",
    EMPLOYEE: (username) => `/admin-secure/api/v1/employees/${encodeURIComponent(username)}`,
    EMPLOYEE_ACCOUNT_STATUS: (username) => `/admin-secure/api/v1/employees/${encodeURIComponent(username)}/account-status`
};

export function getAuthHeaders(includeJson = true) {
    const token = localStorage.getItem("token");
    if (token && isTokenExpired(token)) {
        notifySessionExpired();
        throw new Error("Session expirée, veuillez vous reconnecter");
    }
    return {
        ...(includeJson ? { "Content-Type": "application/json" } : {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {})
    };
}

/**
 * fetch with AbortController timeout and clearer network errors for Chrome/Firefox.
 */
export async function apiFetch(url, options = {}, timeoutMs = API_TIMEOUT_MS) {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);
    const { signal: outerSignal, ...rest } = options;

    const onOuterAbort = () => controller.abort();
    if (outerSignal) {
        if (outerSignal.aborted) {
            controller.abort();
        } else {
            outerSignal.addEventListener("abort", onOuterAbort, { once: true });
        }
    }

    try {
        const response = await fetch(url, { ...rest, signal: controller.signal });
        captureAuthTokenFromResponse(response);
        return response;
    } catch (err) {
        if (err?.name === "AbortError") {
            throw new Error(
                outerSignal?.aborted
                    ? "Requête annulée"
                    : "Le serveur met trop de temps à répondre. Réessayez."
            );
        }
        throw new Error(
            "Connexion interrompue. Vérifiez votre réseau et réessayez."
        );
    } finally {
        window.clearTimeout(timeoutId);
        if (outerSignal) {
            outerSignal.removeEventListener("abort", onOuterAbort);
        }
    }
}

export async function parseResponse(response, context = "") {
    const responseText = await response.text();
    let data;
    try {
        data = responseText ? JSON.parse(responseText) : null;
    } catch {
        console.error("[API] Invalid JSON response", { context, status: response.status });
        throw new Error(response.ok ? "Réponse invalide du serveur" : `Erreur ${response.status}`);
    }

    if (import.meta.env.DEV) {
        console.info("[API] Response", {
            context,
            status: response.status,
            ok: response.ok,
            body: data
        });
    }

    if (response.status === 401 && localStorage.getItem("token")) {
        notifySessionExpired();
        throw new Error("Session expirée, veuillez vous reconnecter");
    }

    if (response.status === 403 && localStorage.getItem("token") && isTokenExpired(localStorage.getItem("token"))) {
        notifySessionExpired();
        throw new Error("Session expirée, veuillez vous reconnecter");
    }

    if (!responseText) {
        if (response.status === 401) {
            notifySessionExpired();
            throw new Error("Session expirée, veuillez vous reconnecter");
        }
        throw new Error(response.ok ? "Réponse vide du serveur" : `Erreur ${response.status}`);
    }

    if (!data.success) {
        throw new Error(data.errors?.[0] || "La requête a échoué");
    }
    return data;
}
